// ProductForm.tsx - merged: main's rich validation + our api.shop separation + myProducts refresh
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useFrontendAuth } from '../hooks/useAuth';
import {
    createProduct,
    updateProduct,
    getProduct,
    getSpecificationsForCategories,
    getAllCategories,
    getShippingSizeBands,
    getPickupLocations,
    uploadProductImages,
} from '../services/api.shop';  // our separation: shop functions via api.shop
import {
    CreateProductDto,
    UpdateProductDto,
    SpecificationDto,
    CategoryDto,
    ProductCondition,
    Product,
    ListingAssistResultDto,
    ShippingSizeBandPublicDto,
} from '../types';
import { useTranslation } from '../context/TranslationContext';
import { translateWithFallback } from '../utils/translateWithFallback';
import { translateProductCondition } from '../utils/productConditionLabel';
import {
    validateProductForm,
    validateImageFiles,
    parseAxiosValidationErrors,
    mergeFieldErrors,
    hasFieldErrors,
    parseMoneyInput,
    formatMoney,
    MAX_TITLE_LENGTH,
    MAX_DESCRIPTION_LENGTH,
    MAX_CATEGORIES,
    MAX_IMAGES,
    MIN_PRICE,
    MAX_PRICE,
    type ProductFieldErrors,
} from '../utils/productValidation';
import { applyPendingFieldsToSpecs, buildAssistDescription } from '../utils/listingAssistMap';
import { translateCategoryName } from '../utils/categoryLabel';
import {
    translateShippingSizeBandHelp,
    translateShippingSizeBandLabel,
} from '../utils/shippingSizeBandLabel';
import { containsBlockedContent } from '../utils/contentModeration';
import { scrollToFirstErrorField } from '../utils/scrollToFirstError';
import { invalidateSellerListingCaches } from '../utils/invalidateSellerListingCaches';
import { ValidationError } from '../admin/components/ValidationError'; // reuse unified nice component
import BackToHomeLink from '../components/BackToHomeLink';
import ShopCheckbox from '../components/ShopCheckbox';
import ShopSelect from '../components/ShopSelect';
import ListingAssistPanel from '../components/listing-assist/ListingAssistPanel';

// alias for local use if needed
const FieldError = ValidationError;

/** Parse comma-separated enum Options from the specification schema. */
function parseEnumOptions(options?: string | null): string[] {
    return (options?.split(',') ?? [])
        .map((o) => o.trim())
        .filter(Boolean);
}

/**
 * Map a stored value onto a configured enum option (case-insensitive).
 * Returns '' when the value is free-form / legacy and not in the list
 * (e.g. product 24 age "6-36" while options are 0-2,2-4,…,30-36).
 */
function resolveEnumOptionValue(raw: string, options: string[]): string {
    const v = (raw ?? '').trim();
    if (!v) return '';
    if (options.length === 0) return v;
    const match = options.find((o) => o.toLowerCase() === v.toLowerCase());
    return match ?? '';
}

/**
 * Drop enum values that are not in the schema so the dropdown and required
 * validation match what the seller sees (empty = must pick a real option).
 */
function scrubEnumSpecsAgainstSchema(
    specifications: { [category: string]: { [specName: string]: string } },
    categories: string[],
    categorySpecifications: { [key: string]: SpecificationDto[] }
): { [category: string]: { [specName: string]: string } } {
    const next: { [category: string]: { [specName: string]: string } } = {
        ...specifications,
    };

    for (const cat of categories) {
        const schema = categorySpecifications[cat] || [];
        if (schema.length === 0) continue;
        const values = { ...(next[cat] || {}) };
        let changed = false;
        for (const spec of schema) {
            if (String(spec.type) !== 'Enum') continue;
            const opts = parseEnumOptions(spec.options);
            if (opts.length === 0) continue;
            const raw = values[spec.name] ?? '';
            if (!raw.trim()) continue;
            const resolved = resolveEnumOptionValue(raw, opts);
            if (resolved !== raw) {
                values[spec.name] = resolved;
                changed = true;
            }
        }
        if (changed) next[cat] = values;
    }

    return next;
}

/**
 * Never surface API type-validation jargon on the sell form.
 * Actionable business messages (sold listing, missing required specs) stay visible.
 */
function toShopFriendlySubmitError(
    message: string,
    t: (key: string, params?: Record<string, unknown>) => string
): string {
    const msg = (message ?? '').trim();
    if (!msg) return t('form.productform.errors.submitfailed');

    const lower = msg.toLowerCase();
    const isTechnical =
        lower.startsWith('invalid boolean') ||
        lower.startsWith('invalid integer') ||
        lower.startsWith('invalid decimal') ||
        lower.includes('not found or not applicable') ||
        lower === 'product not found' ||
        lower === 'seller not found' ||
        // Bare English from older API builds
        lower === 'invalid boolean value' ||
        lower === 'invalid integer value' ||
        lower === 'invalid decimal value';

    if (isTechnical) {
        return t('form.productform.errors.submitfailed');
    }

    // Backend generic English (when translation key missing on client, still readable)
    if (
        lower.includes('could not save your listing') ||
        lower.includes('check the details and try again')
    ) {
        return t('form.productform.errors.submitfailed');
    }

    return msg;
}

/**
 * Ensure every loaded Boolean category spec is sent as "true" | "false".
 * Untouched checkboxes look like "No" in the UI but stay as "" in state — API rejects that.
 */
function normalizeBooleanSpecsForSubmit(
    specifications: { [category: string]: { [specName: string]: string } },
    categories: string[],
    categorySpecifications: { [key: string]: SpecificationDto[] }
): { [category: string]: { [specName: string]: string } } {
    const next: { [category: string]: { [specName: string]: string } } = {};

    for (const cat of categories) {
        const values = { ...(specifications[cat] || {}) };
        for (const spec of categorySpecifications[cat] || []) {
            if (String(spec.type) !== 'Boolean') continue;
            const raw = (values[spec.name] ?? '').toString().trim().toLowerCase();
            if (raw === 'true' || raw === 'yes' || raw === '1' || raw === 'ja' || raw === 'oui' || raw === 'y') {
                values[spec.name] = 'true';
            } else {
                // empty, false, no, nee, … → false (matches checkbox UI default)
                values[spec.name] = 'false';
            }
        }
        next[cat] = values;
    }

    // Keep any leftover groups only if still selected (shouldn't happen after category toggle cleanup)
    for (const [cat, specs] of Object.entries(specifications)) {
        if (!next[cat] && categories.includes(cat)) {
            next[cat] = { ...specs };
        }
    }

    return next;
}

/** Create payload requires price; the form starts empty on new listings (ticket #268). */
type SellFormData = Omit<CreateProductDto, 'price'> & { price?: number };

const ProductForm = () => {
    const { id } = useParams<{ id?: string }>();
    const { t } = useTranslation();
    const isEdit = !!id;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useFrontendAuth();

    const [formData, setFormData] = useState<SellFormData>({
        title: '',
        description: '',
        // Create: empty field (not 0). Edit load overwrites with the listing price.
        price: undefined,
        imageUrls: [],
        condition: ProductCondition.LikeNew,
        quantityAvailable: 1,
        categories: [],
        tags: [],
        specifications: {},
        // Empty until seller picks (or category default). Never prefill M — that skipped required validation.
        packageSizeBandCode: '',
        listingAttested: false,
        pickupLocationIds: [],
    });
    /** True after seller manually picks a package size (stops category default overwriting). */
    const packageSizeTouchedRef = useRef(false);

    const [uploadedImages, setUploadedImages] = useState<{ id: string; file: File; preview: string }[]>([]);
    const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [categories, setCategories] = useState<CategoryDto[]>([]);
    const [sizeBands, setSizeBands] = useState<ShippingSizeBandPublicDto[]>([]);
    const [pickupCities, setPickupCities] = useState<{ id: number; city: string; region?: string | null }[]>([]);
    const [categorySpecifications, setCategorySpecifications] = useState<{ [key: string]: SpecificationDto[] }>({});
    /** Pending OCR fields to merge into specs when categories load (Apply all). */
    const [pendingAssistFields, setPendingAssistFields] = useState<Record<string, string>>({});
    /** True when description was last set by listing assist (safe to recompute on category pick). */
    const [assistDescriptionApplied, setAssistDescriptionApplied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<ProductFieldErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);

    const resolveErrorMessage = useCallback(
        (error: string | undefined): string | undefined => {
            if (!error) return undefined;
            if (error.startsWith('form.')) {
                return t(error, {
                    min: MIN_PRICE.toFixed(2),
                    max: MAX_PRICE.toFixed(2),
                    maxTitle: MAX_TITLE_LENGTH,
                    maxDesc: MAX_DESCRIPTION_LENGTH,
                    maxCategories: MAX_CATEGORIES,
                    maxImages: MAX_IMAGES,
                });
            }
            return error;
        },
        [t]
    );

    const clearFieldError = (field: string) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    // Keep latest values for async loaders without re-running product fetch on language tick.
    const tRef = useRef(t);
    tRef.current = t;
    const pendingAssistFieldsRef = useRef(pendingAssistFields);
    pendingAssistFieldsRef.current = pendingAssistFields;
    const assistDescriptionAppliedRef = useRef(assistDescriptionApplied);
    assistDescriptionAppliedRef.current = assistDescriptionApplied;
    const formCategoriesRef = useRef(formData.categories);
    formCategoriesRef.current = formData.categories;
    const categorySpecificationsRef = useRef(categorySpecifications);
    categorySpecificationsRef.current = categorySpecifications;

    const loadCategorySpecifications = useCallback(async (categoryNames: string[]) => {
        const toFetch = categoryNames.filter(Boolean);
        const fetched: { [key: string]: SpecificationDto[] } = {};
        for (const cat of toFetch) {
            if (categorySpecificationsRef.current[cat]?.length) continue;
            try {
                const specList = await getSpecificationsForCategories([cat]);
                fetched[cat] = specList;
            } catch {
                console.error(`Failed to load specifications for ${cat}`);
            }
        }

        // Merge in this function (not inside setState). After `await`, React 18 does not run
        // the updater before the next lines — assigning mergedSpecs in the setter left {}.
        const mergedSpecs: { [key: string]: SpecificationDto[] } = {
            ...categorySpecificationsRef.current,
        };
        for (const cat of toFetch) {
            if (fetched[cat] && !mergedSpecs[cat]) {
                mergedSpecs[cat] = fetched[cat];
            }
        }
        categorySpecificationsRef.current = mergedSpecs;
        setCategorySpecifications(mergedSpecs);

        // After schema load: scrub invalid enum values, then fill empty spec slots from OCR Apply.
        const pending = pendingAssistFieldsRef.current;
        const assistOwned = assistDescriptionAppliedRef.current;
        const applyCatsGuess = Array.from(
            new Set([...(formCategoriesRef.current ?? []), ...toFetch].filter(Boolean))
        );
        const orphanDesc =
            Object.keys(pending).length > 0
                ? buildAssistDescription(pending, applyCatsGuess, mergedSpecs).slice(
                      0,
                      MAX_DESCRIPTION_LENGTH
                  )
                : '';

        setFormData((prev) => {
            const applyCats =
                (prev.categories ?? []).length > 0 ? (prev.categories ?? []) : toFetch;
            let specs = scrubEnumSpecsAgainstSchema(
                prev.specifications ?? {},
                applyCats,
                mergedSpecs
            );

            if (Object.keys(pending).length > 0) {
                specs = applyPendingFieldsToSpecs(specs, applyCats, mergedSpecs, pending);
            }

            const descEmpty = !(prev.description ?? '').trim();
            let nextDesc = prev.description;
            if (Object.keys(pending).length > 0 && (descEmpty || assistOwned)) {
                if (orphanDesc) {
                    nextDesc = orphanDesc;
                } else if (assistOwned) {
                    nextDesc = '';
                }
            }

            return {
                ...prev,
                specifications: specs,
                description: nextDesc,
            };
        });

        if (Object.keys(pending).length > 0 && (assistOwned || orphanDesc)) {
            setAssistDescriptionApplied(!!orphanDesc);
        }
    }, []);

    // Catalog list + package size bands — independent of product edit load
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await getAllCategories();
                setCategories(cats);
            } catch {
                console.error('Failed to load categories');
            }
        };
        const fetchBands = async () => {
            try {
                const bands = await getShippingSizeBands();
                const list = Array.isArray(bands)
                    ? bands
                    : (bands as { $values?: ShippingSizeBandPublicDto[] })?.$values ?? [];
                setSizeBands(list);
            } catch {
                console.error('Failed to load shipping size bands');
            }
        };
        const fetchPickup = async () => {
            try {
                setPickupCities(await getPickupLocations());
            } catch {
                console.error('Failed to load pickup cities');
            }
        };
        void fetchBands();
        void fetchPickup();
        fetchCategories();
    }, []);

    // Edit: load product once per id. Do NOT depend on `t` — translation bundle updates were
    // re-fetching and wiping in-progress category/spec corrections mid-edit.
    useEffect(() => {
        if (!isEdit || !id) return;

        let cancelled = false;
        getProduct(parseInt(id, 10))
            .then((prod: Product) => {
                if (cancelled) return;
                packageSizeTouchedRef.current = true;
                setFormData({
                    title: prod.title ?? '',
                    description: prod.description ?? '',
                    price: parseMoneyInput(String(prod.price ?? 0)),
                    imageUrls: prod.imageUrls ?? [],
                    condition: prod.condition,
                    quantityAvailable: prod.quantityAvailable ?? 1,
                    categories: prod.categories ?? [],
                    tags: prod.tags ?? [],
                    specifications: prod.specifications ?? {},
                    packageSizeBandCode: prod.packageSizeBandCode ?? '',
                    listingAttested: !!prod.listingAttestedAt,
                    pickupLocationIds: prod.pickupLocationIds ?? [],
                });
                setExistingImageUrls(prod.imageUrls ?? []);
                setUploadedImages([]);
                setPendingAssistFields({});
                setAssistDescriptionApplied(false);
                if (prod.categories && prod.categories.length > 0) {
                    void loadCategorySpecifications(prod.categories);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setSubmitError(tRef.current('form.productform.errors.loadfailed'));
                }
            });

        return () => {
            cancelled = true;
        };
    }, [id, isEdit, loadCategorySpecifications]);

    const handleListingAssistApplyAll = (result: ListingAssistResultDto) => {
        const fields = result.fields ?? {};
        pendingAssistFieldsRef.current = fields;
        setPendingAssistFields(fields);

        const cats = formData.categories ?? [];
        const orphanDesc = buildAssistDescription(fields, cats, categorySpecifications);
        const serverHint = result.descriptionHint?.trim() ?? '';
        const descSource = orphanDesc || serverHint;
        const descEmpty = !(formData.description ?? '').trim();
        const willFillDesc = descEmpty && !!descSource;

        setFormData((prev) => {
            const titleEmpty = !(prev.title ?? '').trim();
            const prevDescEmpty = !(prev.description ?? '').trim();
            const nextTitle =
                titleEmpty && result.suggestedTitle?.trim()
                    ? result.suggestedTitle.trim().slice(0, MAX_TITLE_LENGTH)
                    : prev.title;

            const nextDesc =
                prevDescEmpty && descSource
                    ? descSource.slice(0, MAX_DESCRIPTION_LENGTH)
                    : prev.description;

            const specs = applyPendingFieldsToSpecs(
                prev.specifications ?? {},
                prev.categories ?? [],
                categorySpecifications,
                fields
            );

            return {
                ...prev,
                title: nextTitle,
                description: nextDesc,
                specifications: specs,
            };
        });

        setAssistDescriptionApplied(willFillDesc);

        if (result.suggestedTitle?.trim()) clearFieldError('title');
        if (willFillDesc) clearFieldError('description');
        setSubmitError(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        clearFieldError(name);
        if (name === 'description') {
            setAssistDescriptionApplied(false);
        }
        setFormData((prev) => ({
            ...prev,
            // Empty stays empty (do not coerce to 0). Otherwise exact euro cents (comma or dot).
            [name]:
                name === 'price'
                    ? value.trim() === ''
                        ? undefined
                        : parseMoneyInput(value)
                    : value,
        }));
    };

    const handleCategoryToggle = (catName: string) => {
        const selectedCats = formData.categories ?? [];

        if (selectedCats.includes(catName)) {
            const newCats = selectedCats.filter((c) => c !== catName);
            // Single state update: drop category + its spec values together (avoids partial overwrite races)
            setFormData((prev) => {
                const { [catName]: _, ...remainingSpecs } = prev.specifications ?? {};
                return {
                    ...prev,
                    categories: newCats,
                    specifications: remainingSpecs,
                };
            });
            setCategorySpecifications((prev) => {
                if (!(catName in prev)) return prev;
                const next = { ...prev };
                delete next[catName];
                return next;
            });

            // Clear any spec errors for the removed category
            setFieldErrors((prev) => {
                const next = { ...prev };
                Object.keys(next).forEach((k) => {
                    if (k.startsWith(`${catName}-`)) {
                        delete next[k];
                    }
                });
                return next;
            });
            clearFieldError('categories');
            return;
        }

        if (selectedCats.length >= MAX_CATEGORIES) {
            setFieldErrors((prev) => ({
                ...prev,
                categories: t('form.productform.errors.categories.max', { maxCategories: MAX_CATEGORIES }),
            }));
            return;
        }

        const newCats = [...selectedCats, catName];
        clearFieldError('categories');
        setFormData((prev) => ({ ...prev, categories: newCats }));
        applyCategoryPackageDefault(newCats);
        enforceCategoryPackageMin(newCats);
        void loadCategorySpecifications([catName]);
    };

    const handleSpecChange = (cat: string, name: string, value: string | boolean) => {
        // Functional update so rapid Brand + Age (etc.) edits never clobber each other
        setFormData((prev) => {
            const nestedSpecs = { ...(prev.specifications || {}) };
            nestedSpecs[cat] = { ...(nestedSpecs[cat] || {}), [name]: value.toString() };
            return { ...prev, specifications: nestedSpecs };
        });
        clearFieldError(`${cat}-${name}`);
        if (submitError) setSubmitError(null);
    };

    const renderSpecInput = (cat: string, spec: SpecificationDto) => {
        const catSpecs = formData.specifications?.[cat] || {};
        const value = catSpecs[spec.name] ?? '';
        const specKey = `${cat}-${spec.name}`;
        const hasError = !!fieldErrors[specKey];
        const controlClass = `w-full rounded-xl border px-3 py-2.5 text-sm text-[var(--text-color)] shadow-sm transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--highlight-color,var(--accent-color))] ${
            hasError
                ? 'border-red-300 bg-red-50/40'
                : 'border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_55%)]'
        }`;
        switch (spec.type) {
            case 'Boolean': {
                const checked = ['true', 'yes', 'ja', 'oui', '1', 'y'].includes(
                    value.toString().trim().toLowerCase()
                );
                return (
                    <label className="inline-flex cursor-pointer items-center gap-3 rounded-xl border border-[color-mix(in_srgb,var(--accent-color),transparent_50%)] bg-[color-mix(in_srgb,var(--bg-color),white_40%)] px-4 py-3">
                        <ShopCheckbox
                            checked={checked}
                            onChange={(isChecked) => handleSpecChange(cat, spec.name, isChecked)}
                            aria-label={spec.name}
                        />
                        <span className="text-sm text-[var(--text-color)]">
                            {checked
                                ? translateWithFallback(t, 'common.yes', 'Yes')
                                : translateWithFallback(t, 'common.no', 'No')}
                        </span>
                    </label>
                );
            }
            case 'Enum': {
                const optionValues = parseEnumOptions(spec.options);
                const options = optionValues.map((opt) => ({ value: opt, label: opt }));
                // Invalid legacy value (not in list) → show empty placeholder, not a fake selection
                const selectValue = resolveEnumOptionValue(value, optionValues);
                return (
                    <ShopSelect
                        id={`spec-${cat}-${spec.name}`.replace(/\s+/g, '-')}
                        value={selectValue}
                        onChange={(v) => handleSpecChange(cat, spec.name, v)}
                        options={[
                            { value: '', label: t('form.productform.spec.select') },
                            ...options,
                        ]}
                        ariaLabel={spec.name}
                    />
                );
            }
            case 'Integer':
            case 'Decimal': {
                return (
                    <input
                        type="number"
                        value={value}
                        onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                        required={spec.isRequired}
                        className={controlClass}
                    />
                );
            }
            case 'Date': {
                return (
                    <input
                        type="date"
                        value={value}
                        onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                        required={spec.isRequired}
                        className={controlClass}
                    />
                );
            }
            default: {
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                        required={spec.isRequired}
                        className={controlClass}
                    />
                );
            }
        }
    };

    const getSpecErrors = (): ProductFieldErrors => {
        const specErrors: ProductFieldErrors = {};

        (formData.categories ?? []).forEach((cat) => {
            const catSpecs = categorySpecifications[cat] || [];
            catSpecs.forEach((spec) => {
                const raw = ((formData.specifications ?? {})[cat] ?? {})[spec.name] ?? '';
                // Enum must match a configured option — free-form "6-36" is not a selection
                const value =
                    String(spec.type) === 'Enum'
                        ? resolveEnumOptionValue(raw, parseEnumOptions(spec.options))
                        : raw;
                const fieldKey = `${cat}-${spec.name}`;
                if (spec.isRequired && value.trim() === '') {
                    specErrors[fieldKey] = t('form.productform.errors.spec.required', {
                        name: spec.name,
                        category: cat,
                    });
                } else if (value.trim() && containsBlockedContent(value)) {
                    // Same FieldError / ValidationError style as title & description
                    specErrors[fieldKey] =
                        resolveErrorMessage('form.productform.errors.inappropriate') ||
                        t('form.productform.errors.inappropriate');
                }
            });
        });

        return specErrors;
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        const newFiles = Array.from(e.target.files);
        const total = uploadedImages.length + existingImageUrls.length + newFiles.length;

        if (total > MAX_IMAGES) {
            setFieldErrors((prev) => ({
                ...prev,
                imageUrls: t('form.productform.errors.images.max', { maxImages: MAX_IMAGES }),
            }));
            return;
        }

        const fileErrors = validateImageFiles(newFiles);
        if (hasFieldErrors(fileErrors)) {
            const key = fileErrors.imageUrls;
            setFieldErrors((prev) => ({
                ...prev,
                imageUrls: resolveErrorMessage(key) ?? key ?? '',
            }));
            return;
        }

        const newItems = newFiles.map((file) => ({
            id: crypto.randomUUID(),
            file,
            preview: URL.createObjectURL(file),
        }));
        setUploadedImages((prev) => [...prev, ...newItems]);
        clearFieldError('imageUrls');
        setSubmitError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = (idOrUrl: string) => {
        setUploadedImages((prev) => {
            const image = prev.find((img) => img.id === idOrUrl);
            if (image) URL.revokeObjectURL(image.preview);
            return prev.filter((img) => img.id !== idOrUrl);
        });
        setExistingImageUrls((prev) => prev.filter((url) => url !== idOrUrl));
        clearFieldError('imageUrls');
    };

    const runClientValidation = (): ProductFieldErrors => {
        const imageCount = uploadedImages.length + existingImageUrls.length;
        const rawErrors = validateProductForm({
            title: formData.title ?? '',
            description: formData.description ?? '',
            price: formData.price ?? 0,
            categories: formData.categories ?? [],
            tags: formData.tags ?? [],
            imageCount,
            isCreate: !isEdit,
            specifications: formData.specifications ?? {},
        });

        const resolved: ProductFieldErrors = {};
        for (const [field, key] of Object.entries(rawErrors)) {
            resolved[field] = resolveErrorMessage(key) ?? key;
        }
        const band = (formData as CreateProductDto).packageSizeBandCode?.trim() ?? '';
        if (sizeBands.length === 0) {
            // No admin-configured active bands — cannot satisfy required package size
            resolved.packageSizeBandCode = translateWithFallback(
                t,
                'form.productform.errors.packageSize.unavailable',
                'Package sizes are not configured yet. Please try again later or contact support.'
            );
        } else if (!band) {
            resolved.packageSizeBandCode = translateWithFallback(
                t,
                'form.productform.errors.packageSize.required',
                'Package size is required.'
            );
        } else if (!sizeBands.some((b) => b.code.toUpperCase() === band.toUpperCase())) {
            resolved.packageSizeBandCode = translateWithFallback(
                t,
                'form.productform.errors.packageSize.invalid',
                'Select a valid package size.'
            );
        } else {
            const minCode = resolveStrictestMinBandCode(formData.categories ?? []);
            if (minCode && !bandMeetsMin(band, minCode)) {
                const minBand = sizeBands.find((b) => b.code.toUpperCase() === minCode);
                const minLabel = translateShippingSizeBandLabel(
                    (k, fb) => translateWithFallback(t, k, fb),
                    minCode,
                    minBand?.label ?? minCode
                );
                resolved.packageSizeBandCode = translateWithFallback(
                    t,
                    'form.productform.errors.packageSize.tooSmall',
                    'This category requires package size {size} or larger.'
                ).replace('{size}', minLabel);
            }
        }
        if (!formData.listingAttested) {
            resolved.listingAttested = translateWithFallback(
                t,
                'form.productform.errors.listingAttested.required',
                'Confirm that this listing matches the terms of service.'
            );
        }
        return resolved;
    };

    /** SortOrder-based rank for active bands (higher = larger package). */
    const bandRank = (code: string): number => {
        const idx = sizeBands.findIndex((b) => b.code.toUpperCase() === code.toUpperCase());
        if (idx < 0) return -1;
        return sizeBands[idx].sortOrder * 1000 + idx;
    };

    const bandMeetsMin = (chosen: string, minCode: string) =>
        bandRank(chosen) >= 0 && bandRank(minCode) >= 0 && bandRank(chosen) >= bandRank(minCode);

    /** Strictest optional min among selected categories (empty mins ignored). */
    const resolveStrictestMinBandCode = (selectedCatNames: string[]): string | null => {
        const mins = categories
            .filter((c) => selectedCatNames.includes(c.name))
            .map((c) => c.minPackageSizeBandCode?.trim().toUpperCase())
            .filter((c): c is string => !!c);
        if (mins.length === 0 || sizeBands.length === 0) return null;
        return mins
            .filter((c) => sizeBands.some((b) => b.code.toUpperCase() === c))
            .sort((a, b) => bandRank(b) - bandRank(a))[0] ?? null;
    };

    const applyCategoryPackageDefault = (selectedCatNames: string[]) => {
        if (packageSizeTouchedRef.current || isEdit) return;
        const first = categories.find((c) => selectedCatNames.includes(c.name));
        const code = first?.defaultPackageSizeBandCode?.trim();
        if (!code) return;
        if (sizeBands.length > 0 && !sizeBands.some((b) => b.code.toUpperCase() === code.toUpperCase()))
            return;
        setFormData((prev) => ({ ...prev, packageSizeBandCode: code.toUpperCase() }));
    };

    /** If category min is set and current pick is too small, bump to min. */
    const enforceCategoryPackageMin = (selectedCatNames: string[]) => {
        const minCode = resolveStrictestMinBandCode(selectedCatNames);
        if (!minCode) return;
        setFormData((prev) => {
            const current = (prev as CreateProductDto).packageSizeBandCode?.trim() ?? '';
            if (current && bandMeetsMin(current, minCode)) return prev;
            return { ...prev, packageSizeBandCode: minCode };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        const clientErrors = runClientValidation();
        const specErrors = getSpecErrors();
        const allErrors = mergeFieldErrors(clientErrors, specErrors);

        setFieldErrors(allErrors);

        if (hasFieldErrors(allErrors)) {
            // Pass the full set of errors so the scroll utility can use explicit key matching
            // (in DOM source order) instead of pure style fallback. This ensures that
            // earlier fields (images, title, etc.) are preferred over later dynamic spec fields.
            scrollToFirstErrorField(allErrors);
            return;
        }

        setLoading(true);

        try {
            let finalImageUrls = [...existingImageUrls];

            if (uploadedImages.length > 0) {
                const newUrls = await uploadProductImages(uploadedImages.map((img) => img.file));
                finalImageUrls = [...finalImageUrls, ...newUrls];
            }

            // Scrub invalid enums + normalize booleans before API (legacy free-form ages, empty checkboxes)
            let specifications = scrubEnumSpecsAgainstSchema(
                formData.specifications ?? {},
                formData.categories ?? [],
                categorySpecifications
            );
            specifications = normalizeBooleanSpecsForSubmit(
                specifications,
                formData.categories ?? [],
                categorySpecifications
            );

            const commonData = {
                title: (formData.title ?? '').trim(),
                description: (formData.description ?? '').trim(),
                // Re-normalize so payload is always exact cents
                price: parseMoneyInput(String(formData.price ?? 0)),
                imageUrls: finalImageUrls,
                condition: formData.condition,
                quantityAvailable: formData.quantityAvailable,
                categories: formData.categories ?? [],
                tags: (formData.tags ?? []).map((tag) => tag.trim()).filter(Boolean),
                specifications,
                packageSizeBandCode: (
                    (formData as CreateProductDto).packageSizeBandCode ?? ''
                )
                    .trim()
                    .toUpperCase(),
                listingAttested: !!formData.listingAttested,
                pickupLocationIds: formData.pickupLocationIds ?? [],
            };

            const productId = isEdit && id ? parseInt(id, 10) : NaN;
            let createdId: number | null = null;
            if (isEdit && id) {
                await updateProduct(productId, commonData as UpdateProductDto);
            } else {
                const created = await createProduct(commonData as CreateProductDto);
                createdId = created?.id ?? null;
            }

            // Bust all product-related TanStack caches so search / home / detail show the new price
            // without a hard refresh. Note: detail uses queryKey ['product', id] (singular) \u2014
            // invalidating only ['products'] left detail/search cards stale for up to staleTime.
            await queryClient.invalidateQueries({ queryKey: ['products'] });
            await queryClient.invalidateQueries({ queryKey: ['search'] });
            await queryClient.invalidateQueries({ queryKey: ['product'] });
            await queryClient.invalidateQueries({ queryKey: ['publicVersions'] });

            // Drop cached payloads so remount always refetches (avoids keepPreviousData + long staleTime).
            queryClient.removeQueries({ queryKey: ['search'] });
            queryClient.removeQueries({ queryKey: ['products'] });
            if (!Number.isNaN(productId)) {
                queryClient.removeQueries({ queryKey: ['product', productId] });
            }

            // Private myProducts* (prefix remove for useProtectedQuery + onboarding banner)
            // and public profile/listings for this seller \u2014 refetchOnMount is false on private queries.
            await invalidateSellerListingCaches(queryClient, user?.id, { removePrivate: true });

            setUploadedImages([]);
            setExistingImageUrls([]);
            // After a new listing, land on home with a pulse on the new card (exclude-own stays off by default).
            if (createdId != null) {
                navigate(`/home?highlight=${createdId}`);
            } else {
                navigate('/my-account/listings');
            }
        } catch (err: unknown) {
            const serverErrors = parseAxiosValidationErrors(err);
            if (hasFieldErrors(serverErrors)) {
                // Resolve form.* keys (incl. inappropriate) so FieldError shows translated text
                const resolvedServer: ProductFieldErrors = {};
                for (const [field, msg] of Object.entries(serverErrors)) {
                    resolvedServer[field] = resolveErrorMessage(msg) ?? msg;
                }
                // Surface general / non-field messages at the top — never show developer jargon
                // (e.g. "Invalid boolean value") to sellers.
                const rawGeneral =
                    resolvedServer.general ??
                    resolvedServer.General ??
                    resolvedServer.GeneralError;
                if (rawGeneral) {
                    setSubmitError(toShopFriendlySubmitError(rawGeneral, t));
                    delete resolvedServer.general;
                    delete resolvedServer.General;
                    delete resolvedServer.GeneralError;
                } else {
                    setSubmitError(null);
                }
                const merged = mergeFieldErrors(allErrors, resolvedServer);
                setFieldErrors(merged);
                scrollToFirstErrorField(merged);
            } else if (err instanceof AxiosError && err.response?.status === 400) {
                setSubmitError(t('form.productform.errors.submitfailed'));
            } else {
                setSubmitError(t('form.productform.errors.submitfailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    const inputClass = (field: string) =>
        `border p-3 w-full rounded-md focus:ring-2 focus:ring-[var(--accent-color)] focus:border-transparent ${
            fieldErrors[field] ? 'border-red-300 bg-red-50/30' : 'border-gray-300'
        }`;

    return (
        <div className="max-w-3xl mx-auto py-6 sm:py-10">
            <BackToHomeLink />
            <h1 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">
                {isEdit ? t('page.productform.title.edit') : t('page.productform.title.create')}
            </h1>

            <form noValidate onSubmit={handleSubmit} className="bg-white p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md space-y-6 sm:space-y-8">
                <ValidationError error={submitError} />

                <ListingAssistPanel
                    t={t}
                    disabled={loading}
                    onApplyAll={handleListingAssistApplyAll}
                />

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('form.productform.images.label')}
                    </label>
                    <div
                        data-error-field="imageUrls"
                        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors hover:border-[var(--accent-color)] ${
                            fieldErrors.imageUrls ? 'border-red-300 bg-red-50/20' : 'border-gray-300'
                        }`}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png"
                            multiple
                            onChange={handleImageChange}
                            className="hidden"
                            id="image-upload"
                        />
                        <label htmlFor="image-upload" className="cursor-pointer block">
                            <p className="text-gray-500">{t('form.productform.images.placeholder')}</p>
                            <p className="text-xs text-gray-400 mt-1">{t('form.productform.images.details')}</p>
                        </label>
                    </div>
                    <FieldError error={fieldErrors.imageUrls} />

                    {(existingImageUrls.length > 0 || uploadedImages.length > 0) && (
                        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 mt-6">
                            {existingImageUrls.map((url, index) => (
                                <div key={`existing-${index}`} className="relative group">
                                    <img
                                        src={url}
                                        alt={`Existing ${index + 1}`}
                                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(url)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center hover:bg-red-600"
                                    >
                                        ?
                                    </button>
                                </div>
                            ))}
                            {uploadedImages.map((img) => (
                                <div key={img.id} className="relative group">
                                    <img
                                        src={img.preview}
                                        alt="Preview"
                                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(img.id)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center hover:bg-red-600"
                                    >
                                        ?
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.productform.title.label')}
                    </label>
                    <input
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        maxLength={MAX_TITLE_LENGTH}
                        className={inputClass('title')}
                    />
                    <FieldError error={fieldErrors.title} />
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.productform.description.label')}
                    </label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        className={`${inputClass('description')} h-32`}
                    />
                    <FieldError error={fieldErrors.description} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('form.productform.price.label')}
                        </label>
                        <input
                            type="number"
                            name="price"
                            value={
                                formData.price === undefined || formData.price === null
                                    ? ''
                                    : formData.price
                            }
                            onChange={handleChange}
                            step="0.01"
                            min={0.5}
                            max={5000}
                            inputMode="decimal"
                            className={inputClass('price')}
                        />
                        {formData.price != null && formData.price > 0 && (
                                    <p className="mt-1 text-xs text-gray-500">{'\u20AC'}{formatMoney(formData.price)}</p>
                        )}
                        <FieldError error={fieldErrors.price} />
                    </div>

                    <div className="mb-6">
                        <label
                            className="mb-1 block text-sm font-medium text-gray-700"
                            htmlFor="product-condition"
                        >
                            {t('form.productform.condition.label')}
                        </label>
                        <ShopSelect
                            id="product-condition"
                            value={String(formData.condition ?? ProductCondition.LikeNew)}
                            onChange={(v) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    condition: v as ProductCondition,
                                }))
                            }
                            options={Object.values(ProductCondition).map((cond) => ({
                                value: cond,
                                label: translateProductCondition(
                                    (key, fb) => translateWithFallback(t, key, fb),
                                    cond
                                ),
                            }))}
                            ariaLabel={t('form.productform.condition.label')}
                        />
                    </div>
                </div>

                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                        {t('form.productform.categories.label')}
                        <span className="ml-1 text-xs font-normal text-gray-500">
                            ({t('form.productform.categories.limit', { max: MAX_CATEGORIES })})
                        </span>
                    </label>
                    <div data-error-field="categories" className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {categories.map((cat) => {
                            const isSelected = (formData.categories ?? []).includes(cat.name);
                            const atLimit =
                                !isSelected && (formData.categories ?? []).length >= MAX_CATEGORIES;
                            return (
                                <label
                                    key={cat.id}
                                    className={`flex items-center gap-3 border p-4 rounded-xl cursor-pointer transition ${
                                        atLimit
                                            ? 'cursor-not-allowed opacity-50'
                                            : 'hover:bg-gray-50'
                                    } ${isSelected ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/5' : ''}`}
                                >
                                    <ShopCheckbox
                                        checked={isSelected}
                                        disabled={atLimit}
                                        onChange={() => handleCategoryToggle(cat.name)}
                                        aria-label={translateCategoryName(t, cat.name)}
                                    />
                                    <span className="font-medium">
                                        {translateCategoryName(t, cat.name)}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    <FieldError error={fieldErrors.categories} />
                </div>

                {/* Package size — same grid idea as categories; squarer chips, quiet selected state */}
                <div className="mb-6">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        {translateWithFallback(t, 'form.productform.packageSize.label', 'Package size')}
                        <span className="ml-1 text-red-500" aria-hidden>
                            *
                        </span>
                    </label>
                    <p className="mb-3 text-xs text-gray-500">
                        {translateWithFallback(
                            t,
                            'form.productform.packageSize.help',
                            'Pick the size that fits your packed item. Buyer pays shipping for this size.'
                        )}
                    </p>
                    {(() => {
                        const minCode = resolveStrictestMinBandCode(formData.categories ?? []);
                        if (!minCode) return null;
                        const minBand = sizeBands.find(
                            (b) => b.code.toUpperCase() === minCode
                        );
                        const minLabel = translateShippingSizeBandLabel(
                            (k, fb) => translateWithFallback(t, k, fb),
                            minCode,
                            minBand?.label ?? minCode
                        );
                        return (
                            <p className="mb-2 text-xs text-gray-500">
                                {translateWithFallback(
                                    t,
                                    'form.productform.packageSize.minHint',
                                    'Minimum for selected categories: {size}.'
                                ).replace('{size}', minLabel)}
                            </p>
                        );
                    })()}
                    <div
                        data-error-field="packageSizeBandCode"
                        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                    >
                        {sizeBands.length === 0 ? (
                            <p className="col-span-full rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900">
                                {translateWithFallback(
                                    t,
                                    'form.productform.packageSize.noneConfigured',
                                    'No package sizes are available yet. An admin must add active size bands under Marketplace economics before you can list.'
                                )}
                            </p>
                        ) : (
                            sizeBands.map((band) => {
                                const selected =
                                    ((formData as CreateProductDto).packageSizeBandCode ?? '').toUpperCase() ===
                                    band.code.toUpperCase();
                                const minCode = resolveStrictestMinBandCode(
                                    formData.categories ?? []
                                );
                                const belowMin = !!minCode && !bandMeetsMin(band.code, minCode);
                                return (
                                    <button
                                        key={band.code}
                                        type="button"
                                        disabled={belowMin}
                                        aria-pressed={selected}
                                        onClick={() => {
                                            if (belowMin) return;
                                            packageSizeTouchedRef.current = true;
                                            clearFieldError('packageSizeBandCode');
                                            setFormData((prev) => ({
                                                ...prev,
                                                packageSizeBandCode: band.code.toUpperCase(),
                                            }));
                                        }}
                                        className={`min-h-11 rounded-md px-4 py-3 text-left transition ${
                                            belowMin
                                                ? 'cursor-not-allowed border border-gray-200 bg-gray-50 opacity-50'
                                                : selected
                                                  ? // Same cream as specs category header ("On the road" bar)
                                                    'border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_82%)] shadow-sm'
                                                  : 'border border-gray-300 bg-white hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="flex items-center justify-between gap-2">
                                            <span className="text-sm font-medium text-[var(--text-color)]">
                                                {translateShippingSizeBandLabel(
                                                    (k, fb) => translateWithFallback(t, k, fb),
                                                    band.code,
                                                    band.label
                                                )}
                                            </span>
                                            {selected && !belowMin ? (
                                                <span
                                                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--accent-color),transparent_40%)] text-[11px] font-bold text-[var(--text-color)]"
                                                    aria-hidden
                                                >
                                                    ✓
                                                </span>
                                            ) : null}
                                        </span>
                                        {(() => {
                                            const help = translateShippingSizeBandHelp(
                                                (k, fb) => translateWithFallback(t, k, fb),
                                                band.code,
                                                band.helpText
                                            );
                                            return help ? (
                                                <p className="mt-0.5 text-xs leading-snug text-gray-500">
                                                    {help}
                                                </p>
                                            ) : null;
                                        })()}
                                        <p className="mt-1.5 text-xs text-gray-400">
                                            {translateWithFallback(
                                                t,
                                                'form.productform.packageSize.buyerPays',
                                                'Buyer pays shipping ~€{amount} for this size'
                                            ).replace(
                                                '{amount}',
                                                Number(band.buyerAmount).toFixed(2)
                                            )}
                                        </p>
                                    </button>
                                );
                            })
                        )}
                    </div>
                    <FieldError error={fieldErrors.packageSizeBandCode} />
                </div>

                {pickupCities.length > 0 && (
                    <div className="space-y-2">
                        <h3 className="text-lg font-semibold text-[var(--text-color)]">
                            {translateWithFallback(t, 'form.productform.pickup.title', 'Local pickup')}
                        </h3>
                        <p className="text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                            {translateWithFallback(
                                t,
                                'form.productform.pickup.help',
                                'Optional. Buyers see the city only. Meeting is arranged after they pay on Petito.'
                            )}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {pickupCities.map((loc) => {
                                const selected = (formData.pickupLocationIds ?? []).includes(loc.id);
                                const atCap = (formData.pickupLocationIds ?? []).length >= 3 && !selected;
                                return (
                                    <button
                                        key={loc.id}
                                        type="button"
                                        disabled={atCap}
                                        onClick={() => {
                                            setFormData((prev) => {
                                                const cur = prev.pickupLocationIds ?? [];
                                                const next = selected
                                                    ? cur.filter((id) => id !== loc.id)
                                                    : [...cur, loc.id].slice(0, 3);
                                                return { ...prev, pickupLocationIds: next };
                                            });
                                        }}
                                        className={`min-h-11 rounded-full border px-3 py-1.5 text-sm ${
                                            selected
                                                ? 'border-[var(--accent-color)] bg-[var(--accent-color)]/20 font-semibold'
                                                : 'border-[color-mix(in_srgb,var(--text-color),transparent_70%)]'
                                        }`}
                                    >
                                        {loc.city}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Tags — not used in catalog cards/search/filters; keep model + payload for later
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('form.productform.tags.label')}
                    </label>
                    <input
                        name="tags"
                        value={(formData.tags ?? []).join(', ')}
                        onChange={(e) => {
                            clearFieldError('tags');
                            const tags = e.target.value
                                .split(',')
                                .map((tag) => tag.trim())
                                .filter(Boolean);
                            setFormData((prev) => ({ ...prev, tags }));
                        }}
                        className={inputClass('tags')}
                        placeholder={t('form.productform.tags.placeholder')}
                    />
                    <FieldError error={fieldErrors.tags} />
                </div>
                */}

                {(formData.categories ?? []).length > 0 && (
                    <div className="space-y-5">
                        <div>
                            <h3 className="text-lg font-semibold text-[var(--text-color)]">
                                {t('form.productform.specifications')}
                            </h3>
                            <p className="mt-1 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                {t('form.productform.specs.hint') === 'form.productform.specs.hint'
                                    ? 'Fill in the details buyers need to choose with confidence.'
                                    : t('form.productform.specs.hint')}
                            </p>
                        </div>
                        {(formData.categories ?? []).map((cat) => {
                            const specs = categorySpecifications[cat] || [];
                            const requiredCount = specs.filter((s) => s.isRequired).length;
                            return (
                                <section
                                    key={cat}
                                    className="overflow-visible rounded-2xl border border-[color-mix(in_srgb,var(--accent-color),transparent_45%)] bg-[color-mix(in_srgb,var(--bg-color),white_35%)] shadow-sm"
                                >
                                    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-[color-mix(in_srgb,var(--accent-color),transparent_55%)] bg-[color-mix(in_srgb,var(--accent-color),transparent_82%)] px-4 py-3 sm:px-5">
                                        <div>
                                            <h4 className="font-semibold text-[var(--text-color)]">
                                                {translateCategoryName(t, cat)}
                                            </h4>
                                            <p className="text-xs text-[color-mix(in_srgb,var(--text-color),transparent_40%)]">
                                                {(() => {
                                                    // Must use translated label (Onderweg), never raw API name (Transport)
                                                    const catLabel = translateCategoryName(t, cat);
                                                    const line = t('form.productform.specs.forCategory', {
                                                        category: catLabel,
                                                    });
                                                    return line === 'form.productform.specs.forCategory'
                                                        ? `Details for ${catLabel}`
                                                        : line;
                                                })()}
                                            </p>
                                        </div>
                                        {requiredCount > 0 && (
                                            <span className="rounded-full bg-[var(--accent-color)]/40 px-2.5 py-1 text-xs font-medium text-[var(--text-color)]">
                                                {requiredCount}{' '}
                                                {t('form.productform.specs.requiredBadge') ===
                                                'form.productform.specs.requiredBadge'
                                                    ? 'Required'
                                                    : t('form.productform.specs.requiredBadge')}
                                            </span>
                                        )}
                                    </header>
                                    <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
                                        {specs.map((spec) => {
                                            const fieldKey = `${cat}-${spec.name}`;
                                            const hasError = !!fieldErrors[fieldKey];
                                            return (
                                                <div
                                                    key={spec.name}
                                                    data-error-field={fieldKey}
                                                    className={`rounded-xl border p-3 sm:p-4 transition ${
                                                        hasError
                                                            ? 'border-red-300 bg-red-50/30'
                                                            : 'border-transparent bg-[color-mix(in_srgb,var(--bg-color),white_50%)]'
                                                    } ${spec.type === 'Boolean' ? 'sm:col-span-2' : ''}`}
                                                >
                                                    <label className="mb-2 flex flex-wrap items-center gap-2 text-sm font-medium text-[var(--text-color)]">
                                                        <span>{spec.name}</span>
                                                        {spec.isRequired && (
                                                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700">
                                                                {t('form.productform.specs.requiredBadge') ===
                                                                'form.productform.specs.requiredBadge'
                                                                    ? 'Required'
                                                                    : t('form.productform.specs.requiredBadge')}
                                                            </span>
                                                        )}
                                                    </label>
                                                    {renderSpecInput(cat, spec)}
                                                    <FieldError error={fieldErrors[fieldKey]} />
                                                </div>
                                            );
                                        })}
                                        {specs.length === 0 && (
                                            <p className="sm:col-span-2 text-sm text-[color-mix(in_srgb,var(--text-color),transparent_45%)]">
                                                {"\u2014"}
                                            </p>
                                        )}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                )}

                <div data-error-field="listingAttested" className="space-y-2">
                    <div className="flex items-start gap-3 text-sm text-[var(--text-color)]">
                        <ShopCheckbox
                            id="listing-attested"
                            name="listingAttested"
                            checked={!!formData.listingAttested}
                            onChange={(checked) => {
                                clearFieldError('listingAttested');
                                setFormData((prev) => ({ ...prev, listingAttested: checked }));
                            }}
                            aria-label={translateWithFallback(
                                t,
                                'form.productform.listingAttested.aria',
                                'Confirm listing matches the terms'
                            )}
                        />
                        <span className="min-w-0 flex-1 leading-snug">
                            <label htmlFor="listing-attested">
                                {translateWithFallback(
                                    t,
                                    'form.productform.listingAttested.label',
                                    'I confirm this listing matches the terms of service (including washed textiles and an honest condition).'
                                )}
                            </label>{' '}
                            <Link
                                to="/terms"
                                className="font-semibold text-[var(--accent-color)] underline underline-offset-2"
                            >
                                {translateWithFallback(t, 'page.terms.title', 'Terms of Service')}
                            </Link>
                        </span>
                    </div>
                    <FieldError error={fieldErrors.listingAttested} />
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[var(--accent-color)] text-white py-4 rounded-xl font-semibold text-lg disabled:opacity-50 hover:opacity-90 transition"
                >
                    {loading
                        ? t('state.saving')
                        : isEdit
                          ? t('form.productform.submit.update')
                          : t('form.productform.submit.create')}
                </button>
            </form>
        </div>
    );
};

export default ProductForm;
